"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { SoldPackageStatusBadge, isSoldPackageExpired } from "@/components/billing/sold-package-status-badge";
import { ProgressBarLink } from "@/components/progress-bar";
import { formatCurrency, formatDate } from "@/lib/format-data";
import { PackageStatus } from "@/types/enums/package-status";
import type { PatientSoldPackage } from "@/types/inventory/package";
import { BenefitUsageList } from "./benefit-usage-list";
import { PatientSidebarSectionSkeleton } from "./section-skeleton";
import { listPatientSoldPackages } from "@/api/provider/inventory/package";
import { patientSoldPackageQueryKeys } from "@/api/provider/inventory/package-query";
interface Props {
  patientId: string;
}

/**
 * The package's benefits that this patient can still use. A package can stay
 * Active past its expiry date, and a transfer copies the package link onto the
 * recipient's new benefit, so both are filtered out here.
 */
function currentPackageBenefits(soldPackage: PatientSoldPackage, patientId: string) {
  if (isSoldPackageExpired(soldPackage)) return [];
  return soldPackage.discounts.filter(discount => !discount.patientId || discount.patientId === patientId);
}
export default function PackagesSection(t0) {
  const $ = _c(19);
  const {
    patientId
  } = t0;
  let t1;
  if ($[0] !== patientId) {
    t1 = patientSoldPackageQueryKeys.active(patientId);
    $[0] = patientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== patientId) {
    t2 = () => listPatientSoldPackages(patientId, {
      status: [PackageStatus.ACTIVE]
    });
    $[2] = patientId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !!patientId;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: t5,
    isPending
  } = useQuery(t4);
  let t6;
  if ($[8] !== t5) {
    t6 = t5 === undefined ? [] : t5;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const soldPackages = t6;
  if (isPending) {
    let t7;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <PatientSidebarSectionSkeleton />;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    return t7;
  }
  if (!soldPackages || soldPackages.length === 0) {
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <p className="text-caption text-muted-foreground">No active packages</p>;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  let t7;
  if ($[12] !== patientId || $[13] !== soldPackages) {
    let t8;
    if ($[15] !== patientId) {
      t8 = soldPackage => <ProgressBarLink key={soldPackage.id} href={`/patient/${patientId}?tab=packages`} className="mb-3 block rounded-md p-2 transition-colors hover:bg-hover"><div className="flex items-center justify-between gap-2"><span className="truncate text-body-strong">{soldPackage.package.name}</span><SoldPackageStatusBadge soldPackage={soldPackage} className="flex-shrink-0" /></div><div className="mt-1 flex items-center justify-between text-caption text-foreground-secondary"><span>Purchased {formatDate(soldPackage.createdDate)}</span><span>{formatCurrency(soldPackage.chargeItem.price)}</span></div><BenefitUsageList benefits={currentPackageBenefits(soldPackage, patientId)} /></ProgressBarLink>;
      $[15] = patientId;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    t7 = soldPackages.map(t8);
    $[12] = patientId;
    $[13] = soldPackages;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = <>{t7}</>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}